import { useContext, useEffect, useMemo } from 'react'
import {
  GetAttentionOrdersSubscription,
  GetAttentionOrdersSubscriptionVariables,
  GetAvailableLaboratoriesSubscription,
  GetAvailableLaboratoriesSubscriptionVariables,
  GetAvailableTestsMostPopularSubscription,
  GetAvailableTestsMostPopularSubscriptionVariables,
  GetAvailableTestTagsSubscription,
  GetAvailableTestTagsSubscriptionVariables,
  GetClientFavouriteTestsSubscription,
  GetClientFavouriteTestsSubscriptionVariables,
  GetProfilesSubscription,
  GetProfilesSubscriptionVariables,
  useGetAttentionOrdersSubscription,
  useGetAvailableLaboratoriesSubscription,
  useGetAvailableTestsMostPopularSubscription,
  useGetAvailableTestTagsSubscription,
  useGetClientFavouriteTestsSubscription,
  useGetProfilesSubscription,
} from '../generated/urql.client'
import { PrimitiveAtom, useSetAtom } from 'jotai'
import {
  attentionOrdersSubscriptionAtom,
  availableLaboratoriesSubscriptionAtom,
  availableTestsMostPopularSubscriptionAtom,
  availableTestTagsSubscriptionAtom,
  clientFavouriteTestsSubscriptionAtom,
  consultationsSubscriptionAtom,
  featureFlagsSubscriptionAtom,
  profilesSubscriptionAtom,
  trainingsSubscriptionAtom,
} from '../store/subscription.atoms'
import { Point } from '../lib/geography'
import {
  AnyVariables,
  UseSubscriptionArgs,
  UseSubscriptionResponse,
} from 'urql'
import { BasketContext, isTestBasketItem } from '../provider/basket'
import {
  GetConsultationsSubscription,
  GetConsultationsSubscriptionVariables,
  GetFeatureFlagsSubscription,
  GetFeatureFlagsSubscriptionVariables,
  GetTrainingsSubscription,
  GetTrainingsSubscriptionVariables,
  useGetConsultationsSubscription,
  useGetFeatureFlagsSubscription,
  useGetTrainingsSubscription,
} from '../generated/urql.anonymous'
import { AuthContext } from '../provider/auth'

type HookSignature<TData, TVars extends AnyVariables> = (
  options: Omit<UseSubscriptionArgs<TVars>, 'query'>
) => UseSubscriptionResponse<TData, TVars>

const useSubscriptionProxy = <TData, TVars extends AnyVariables>(
  atom: PrimitiveAtom<UseSubscriptionResponse<TData, TVars>>,
  useSubHook: HookSignature<TData, TVars>,
  options: Omit<UseSubscriptionArgs<TVars>, 'query'>
) => {
  const setValue = useSetAtom(atom)
  const result = useSubHook(options)
  useEffect(() => {
    setValue(result)
  }, [result, setValue])
}

const useSubscriptions = () => {
  const { isLogged } = useContext(AuthContext)
  const { address, items } = useContext(BasketContext)

  const testIds = useMemo(
    () => items.filter(isTestBasketItem).map((item) => item.test.id),
    [items]
  )

  const point: Point = useMemo(
    () =>
      address?.location ?? {
        type: 'Point',
        coordinates: [0, 0],
      },
    [address?.location]
  )

  useSubscriptionProxy<
    GetProfilesSubscription,
    GetProfilesSubscriptionVariables
  >(profilesSubscriptionAtom, useGetProfilesSubscription, {
    pause: !isLogged,
  })

  useSubscriptionProxy<
    GetAvailableTestTagsSubscription,
    GetAvailableTestTagsSubscriptionVariables
  >(availableTestTagsSubscriptionAtom, useGetAvailableTestTagsSubscription, {
    variables: {
      point,
    },
  })

  useSubscriptionProxy<
    GetAttentionOrdersSubscription,
    GetAttentionOrdersSubscriptionVariables
  >(attentionOrdersSubscriptionAtom, useGetAttentionOrdersSubscription, {})

  useSubscriptionProxy<
    GetAvailableLaboratoriesSubscription,
    GetAvailableLaboratoriesSubscriptionVariables
  >(
    availableLaboratoriesSubscriptionAtom,
    useGetAvailableLaboratoriesSubscription,
    {
      pause: !address,
      variables: {
        point,
        limit: 1000,
        testIds,
      },
    }
  )

  useSubscriptionProxy<
    GetAvailableTestsMostPopularSubscription,
    GetAvailableTestsMostPopularSubscriptionVariables
  >(
    availableTestsMostPopularSubscriptionAtom,
    useGetAvailableTestsMostPopularSubscription,
    {
      pause: !address,
      variables: {
        point: point,
        limit: 1000,
      },
    }
  )

  useSubscriptionProxy<
    GetConsultationsSubscription,
    GetConsultationsSubscriptionVariables
  >(consultationsSubscriptionAtom, useGetConsultationsSubscription, {})

  useSubscriptionProxy<
    GetTrainingsSubscription,
    GetTrainingsSubscriptionVariables
  >(trainingsSubscriptionAtom, useGetTrainingsSubscription, {})

  useSubscriptionProxy<
    GetClientFavouriteTestsSubscription,
    GetClientFavouriteTestsSubscriptionVariables
  >(
    clientFavouriteTestsSubscriptionAtom,
    useGetClientFavouriteTestsSubscription,
    {
      variables: {
        point,
      },
      pause: !isLogged,
    }
  )

  useSubscriptionProxy<
    GetFeatureFlagsSubscription,
    GetFeatureFlagsSubscriptionVariables
  >(featureFlagsSubscriptionAtom, useGetFeatureFlagsSubscription, {})
}

export default useSubscriptions
